import { PublicCheckoutPaymentMethod } from '@/api/public'
import {
  Column,
  FormAddressAutocomplete,
  FormCheckBox,
  Loader,
  Typography,
} from '@/components'
import { PaymentMethodList } from '@/modules/checkout/components/PaymentMethodList'
import { FormProvider, useFormContext } from '@/providers'
import {
  usePublicCheckoutCompany,
  usePublicCheckoutPaymentMethod,
  useSavePublicCheckoutPaymentMethod,
  useUpdatePublicCheckoutCompany,
  useUpdatePublicCheckoutPaymentMethod,
} from '@/services'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { makeCompanyDetailsAndPaymentValidationSchema } from './validationSchema'
import { ThemeColor } from '@/enums'

interface Address {
  addressId: number
  street: string
  streetNr: string
  postalCode: string
  city: string
}

interface CompanyDetailsAndPaymentStepFormValues {
  sepa: boolean
  newPaymentName: string
  newPaymentIban: string
  newPaymentIsDefault: boolean
  payment: {
    paymentId: string
  }
  companyDetails: {
    name: string
    address: Address
  }
}

const CompanyDetailsForm = () => {
  const { t } = useTranslation()

  return (
    <Column gap="8px">
      <Column gap="4px">
        <Typography bold variant="h4">
          {t('checkout.titles.companyDetails')}
        </Typography>
        <Typography color={ThemeColor.glass500} variant="description">
          {t('checkout.subtitles.companyDetails')}
        </Typography>
      </Column>
      <FormAddressAutocomplete name="companyDetails" />
    </Column>
  )
}

const PaymentForm = ({
  payments,
  handleCreatePayment,
  isLoading,
}: {
  payments: PublicCheckoutPaymentMethod[]
  handleCreatePayment: (callback: () => void) => void
  isLoading: boolean
}) => {
  const { t } = useTranslation()

  return (
    <Column gap="8px">
      <Column gap="4px">
        <Typography bold variant="h4">
          {t('checkout.titles.payment')}
        </Typography>
      </Column>
      <Column gap="24px">
        <PaymentMethodList
          payments={payments}
          onCreatePaymentSubmit={handleCreatePayment}
          isLoading={isLoading}
          hideDefaultPaymentCheckbox
          showEditButton={!!payments.length}
        />
        <FormCheckBox id="sepa" label={t('sepa')} name="sepa" noMargin />
      </Column>
    </Column>
  )
}

interface SetButtonStateParams {
  onClick?: () => Promise<void>
  loading?: boolean
}

interface PublicCompanyDetailsAndPaymentStepProps {
  onNext: () => void
  setButtonState: (buttonState: SetButtonStateParams) => void
}

interface StepFormProps {
  setButtonState: (buttonState: SetButtonStateParams) => void
  onNext: () => void
}

const StepForm = ({ setButtonState, onNext }: StepFormProps) => {
  const { data } = usePublicCheckoutPaymentMethod()
  const {
    customValidate,
    values,
    setFieldValue,
  } = useFormContext<CompanyDetailsAndPaymentStepFormValues>()

  const createPaymentMethodMutation = useSavePublicCheckoutPaymentMethod()
  const updatePaymentMethodMutation = useUpdatePublicCheckoutPaymentMethod(
    data?.paymentId ?? '',
  )

  const updateCompanyMutation = useUpdatePublicCheckoutCompany()

  const areMutationsLoading =
    createPaymentMethodMutation.isLoading ||
    updatePaymentMethodMutation.isLoading ||
    updateCompanyMutation.isLoading

  useEffect(() => {
    setButtonState({
      onClick: () =>
        customValidate({
          fields: ['companyDetails', 'payment', 'sepa'],
          onSuccess: () => {
            updateCompanyMutation.mutate(
              {
                name: values.companyDetails?.name,
                address: values.companyDetails?.address,
              },
              {
                onSuccess: () => {
                  onNext()
                },
              },
            )
          },
        }),
    })
  }, [values])

  useEffect(() => {
    setButtonState({
      loading: areMutationsLoading,
    })
  }, [areMutationsLoading])

  useEffect(() => {
    if (data?.paymentId) {
      setFieldValue('payment.paymentId', data.paymentId)
    }
  }, [data?.paymentId])

  const handleCreatePayment = (callback: () => void) => {
    const mutation = data?.paymentId
      ? updatePaymentMethodMutation
      : createPaymentMethodMutation

    customValidate({
      fields: ['newPaymentName', 'newPaymentIban'],
      onSuccess: async () => {
        await mutation.mutateAsync({
          name: values.newPaymentName,
          iban: values.newPaymentIban,
        })
        callback()
      },
    })
  }

  return (
    <Column gap="24px">
      <CompanyDetailsForm />
      <PaymentForm
        payments={data ? [data] : []}
        handleCreatePayment={handleCreatePayment}
        isLoading={areMutationsLoading}
      />
    </Column>
  )
}

export const PublicCompanyDetailsAndPaymentStep = ({
  setButtonState,
  onNext,
}: PublicCompanyDetailsAndPaymentStepProps) => {
  const { t } = useTranslation()
  const {
    data: companyData,
    isLoading: isCompanyLoading,
  } = usePublicCheckoutCompany()
  const { data, isLoading } = usePublicCheckoutPaymentMethod()

  const createPaymentMethodMutation = useSavePublicCheckoutPaymentMethod()
  const updatePaymentMethodMutation = useUpdatePublicCheckoutPaymentMethod(
    data?.paymentId ?? '',
  )

  useEffect(() => {
    setButtonState({
      loading:
        createPaymentMethodMutation.isLoading ||
        updatePaymentMethodMutation.isLoading,
    })
  }, [
    createPaymentMethodMutation.isLoading,
    updatePaymentMethodMutation.isLoading,
  ])

  if (isLoading || isCompanyLoading) return <Loader />

  return (
    <div className="flex flex-col gap-6">
      <FormProvider
        validationSchema={makeCompanyDetailsAndPaymentValidationSchema(
          t('validations', { returnObjects: true }),
        )}
        initialValues={{
          companyDetails: {
            name: companyData?.name,
            address: companyData?.address,
          },
          payment: {
            paymentId: data?.paymentId,
          },
        }}
      >
        <StepForm setButtonState={setButtonState} onNext={onNext} />
      </FormProvider>
    </div>
  )
}
